import {
  collection, doc, getDoc, getDocs, query, where, orderBy, limit, onSnapshot,
  serverTimestamp, setDoc, addDoc, updateDoc, deleteDoc, type DocumentData, type QueryConstraint,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  AuditDoc, Organization, UserProfile, QuestionnaireDoc, Invitation, ReleaseDoc, ExportDoc, ReleaseOs, LicenseDoc,
  Ticket, TicketMessage, TicketStatus,
} from '@/types/models';

// ─── User profile ─────────────────────────────────────────────
export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? ({ uid, ...snap.data() } as UserProfile) : null;
}

export async function ensureUserProfile(uid: string, email: string, displayName: string | null): Promise<void> {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      email,
      displayName: displayName ?? email.split('@')[0],
      orgId: null,
      role: 'org_member',
      locale: 'fr',
      createdAt: serverTimestamp(),
    });
  }
}

export interface AssumedOrgSignal {
  assumedOrgId: string | null;
  updatedAt?: import('firebase/firestore').Timestamp;
}

/**
 * Watch the per-user "assumedOrg" signal doc. Written server-side whenever an
 * admin assumes / releases acting as an org. Lets every open session of that
 * admin refresh its ID token so the new `assumedOrgId` claim propagates without
 * forcing a re-login. `cb` receives `null` when the doc does not exist (no
 * assumed org).
 */
export function watchAssumedOrgSignal(uid: string, cb: (sig: AssumedOrgSignal | null) => void): () => void {
  return onSnapshot(
    doc(db, 'users', uid, 'sessions', 'assumedOrg'),
    s => cb(s.exists() ? (s.data() as AssumedOrgSignal) : null),
    () => cb(null),
  );
}

// ─── Organizations ────────────────────────────────────────────
export async function getOrg(orgId: string): Promise<Organization | null> {
  const snap = await getDoc(doc(db, 'organizations', orgId));
  return snap.exists() ? ({ id: orgId, ...snap.data() } as Organization) : null;
}

export async function listAllOrgs(): Promise<Organization[]> {
  const snap = await getDocs(query(collection(db, 'organizations'), orderBy('displayName')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Organization));
}

// ─── Licences (admin) ─────────────────────────────────────────
export async function listLicenses(): Promise<LicenseDoc[]> {
  const snap = await getDocs(collection(db, 'licenses'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() } as LicenseDoc))
    .sort((a, b) => a.orgName.localeCompare(b.orgName) || a.deviceId.localeCompare(b.deviceId));
}

/** Id de la licence qui utilise déjà ce deviceId, ou null. Sert à refuser les doublons. */
export async function findLicenseIdByDeviceId(deviceId: string): Promise<string | null> {
  const snap = await getDocs(query(collection(db, 'licenses'), where('deviceId', '==', deviceId), limit(1)));
  return snap.empty ? null : snap.docs[0].id;
}

export type LicenseInput = Omit<LicenseDoc, 'id' | 'createdAt' | 'updatedAt'>;

export async function createLicense(input: LicenseInput): Promise<string> {
  const ref = await addDoc(collection(db, 'licenses'), {
    ...input,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateLicense(id: string, input: LicenseInput): Promise<void> {
  await updateDoc(doc(db, 'licenses', id), { ...input, updatedAt: serverTimestamp() });
}

export async function deleteLicense(id: string): Promise<void> {
  await deleteDoc(doc(db, 'licenses', id));
}

// ─── Audits ───────────────────────────────────────────────────
export function watchAuditsForOrg(
  orgId: string,
  cb: (audits: AuditDoc[]) => void,
  opts: { limit?: number } = {},
): () => void {
  const constraints: QueryConstraint[] = [
    where('orgId', '==', orgId),
    orderBy('createdAt', 'desc'),
  ];
  if (opts.limit) constraints.push(limit(opts.limit));
  const q = query(collection(db, 'audits'), ...constraints);
  return onSnapshot(q, snap => {
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as AuditDoc)));
  });
}

export function watchAudit(auditId: string, cb: (a: AuditDoc | null) => void): () => void {
  return onSnapshot(doc(db, 'audits', auditId), s =>
    cb(s.exists() ? ({ id: s.id, ...s.data() } as AuditDoc) : null),
  );
}

export async function getAllAuditModules(auditId: string): Promise<Record<string, DocumentData>> {
  const snap = await getDocs(collection(db, 'audits', auditId, 'modules'));
  const out: Record<string, DocumentData> = {};
  snap.forEach(d => { out[d.id] = d.data(); });
  return out;
}

/** Update audit metadata. Futur implementation if the admin want to update the audit metadata like add notes, tags, etc. */
// export async function updateAuditMeta(auditId: string, patch: Partial<AuditDoc>): Promise<void> {
//   await updateDoc(doc(db, 'audits', auditId), { ...patch, updatedAt: serverTimestamp() });
// }

// ─── Questionnaires ───────────────────────────────────────────
export function watchQuestionnaire(
  id: string,
  cb: (q: QuestionnaireDoc | null) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, 'questionnaires', id),
    s => cb(s.exists() ? ({ id: s.id, ...s.data() } as QuestionnaireDoc) : null),
    err => onError?.(err as Error),
  );
}

export async function listOrgQuestionnaires(orgId: string): Promise<QuestionnaireDoc[]> {
  const snap = await getDocs(query(
    collection(db, 'questionnaires'),
    where('orgId', '==', orgId),
    orderBy('updatedAt', 'desc'),
  ));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as QuestionnaireDoc));
}

/** True when the org has at least one submitted AI Readiness questionnaire. */
export async function orgHasSubmittedQuestionnaire(orgId: string): Promise<boolean> {
  const snap = await getDocs(query(
    collection(db, 'questionnaires'),
    where('orgId', '==', orgId),
    where('status', '==', 'submitted'),
    limit(1),
  ));
  return !snap.empty;
}

export async function createQuestionnaire(
  orgId: string,
  filledByUid: string,
  onBehalf: boolean,
): Promise<string> {
  const ref = await addDoc(collection(db, 'questionnaires'), {
    orgId,
    filledByUid,
    filledOnBehalfOfClient: onBehalf,
    status: 'draft',
    data: {},
    locale: 'fr',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function saveQuestionnaireData(id: string, data: Record<string, unknown>): Promise<void> {
  await updateDoc(doc(db, 'questionnaires', id), { data, updatedAt: serverTimestamp() });
}

export async function submitQuestionnaire(id: string): Promise<void> {
  await updateDoc(doc(db, 'questionnaires', id), {
    status: 'submitted',
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// ─── Invitations ──────────────────────────────────────────────
export function watchOrgInvitations(orgId: string, cb: (inv: Invitation[]) => void): () => void {
  const q = query(
    collection(db, 'invitations'),
    where('orgId', '==', orgId),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, s => cb(s.docs.map(d => ({ id: d.id, ...d.data() } as Invitation))));
}

// ─── Members ─────────────────────────────────────────────────
export interface OrgMember {
  uid: string;
  role: 'org_admin' | 'org_member';
  joinedAt?: import('firebase/firestore').Timestamp;
  email?: string;
  displayName?: string;
}

export function watchOrgMembers(orgId: string, cb: (m: OrgMember[]) => void): () => void {
  const ref = collection(db, 'organizations', orgId, 'members');
  return onSnapshot(ref, async snap => {
    const members: OrgMember[] = [];
    for (const d of snap.docs) {
      const data = d.data() as Omit<OrgMember, 'uid' | 'email' | 'displayName'>;
      const uref = doc(db, 'users', d.id);
      const usnap = await getDoc(uref);
      const u = usnap.exists() ? usnap.data() : {};
      members.push({
        uid: d.id,
        role: data.role,
        joinedAt: data.joinedAt,
        email: u.email,
        displayName: u.displayName,
      });
    }
    cb(members);
  });
}

// ─── Releases ─────────────────────────────────────────────────
export async function getLatestRelease(channel: 'stable' | 'beta' = 'stable'): Promise<ReleaseDoc | null> {
  const byOs = await listLatestReleasesByOs(channel);
  return Object.values(byOs)[0] ?? null;
}

/** Latest release per OS for a channel (first hit per OS wins — docs are newest-first). */
export async function listLatestReleasesByOs(
  channel: 'stable' | 'beta' = 'stable',
): Promise<Partial<Record<ReleaseOs, ReleaseDoc>>> {
  const snap = await getDocs(query(
    collection(db, 'releases'),
    where('channel', '==', channel),
    orderBy('publishedAt', 'desc'),
    limit(30),
  ));
  const out: Partial<Record<ReleaseOs, ReleaseDoc>> = {};
  for (const d of snap.docs) {
    const r = { id: d.id, ...d.data() } as ReleaseDoc;
    if (!out[r.os]) out[r.os] = r;
  }
  return out;
}

// ─── Exports ──────────────────────────────────────────────────
export function watchAuditExports(auditId: string, cb: (e: ExportDoc[]) => void): () => void {
  const q = query(
    collection(db, 'audits', auditId, 'exports'),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, s => cb(s.docs.map(d => ({ id: d.id, ...d.data() } as ExportDoc))));
}

// ─── Documents (generic org documents: scopes, uploaded reports, etc.) ───────
import type { DocumentDoc } from '@/types/models';

export function watchOrgDocuments(
  orgId: string,
  cb: (docs: DocumentDoc[]) => void,
): () => void {
  const q = query(
    collection(db, 'documents'),
    where('orgId', '==', orgId),
    where('publishedToClient', '==', true),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as DocumentDoc))),
    () => cb([]),
  );
}

// Admin: watch all documents for an org (including unpublished)
export function watchOrgDocumentsAdmin(
  orgId: string,
  cb: (docs: DocumentDoc[]) => void,
): () => void {
  const q = query(
    collection(db, 'documents'),
    where('orgId', '==', orgId),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as DocumentDoc))),
    () => cb([]),
  );
}

// ─── Support Tickets ──────────────────────────────────────────

/** Watch all tickets for an org (all members see all org tickets). */
export function watchTicketsForOrg(
  orgId: string,
  cb: (tickets: Ticket[]) => void,
): () => void {
  const q = query(
    collection(db, 'tickets'),
    where('orgId', '==', orgId),
    orderBy('updatedAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as Ticket))),
    () => cb([]),
  );
}

/** Watch tickets for an org filtered by status. */
export function watchTicketsForOrgByStatus(
  orgId: string,
  status: TicketStatus,
  cb: (tickets: Ticket[]) => void,
): () => void {
  const q = query(
    collection(db, 'tickets'),
    where('orgId', '==', orgId),
    where('status', '==', status),
    orderBy('updatedAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as Ticket))),
    () => cb([]),
  );
}

/** Watch a single ticket document. */
export function watchTicket(
  ticketId: string,
  cb: (ticket: Ticket | null) => void,
): () => void {
  return onSnapshot(
    doc(db, 'tickets', ticketId),
    s => cb(s.exists() ? ({ id: s.id, ...s.data() } as Ticket) : null),
  );
}

/** Watch all messages of a ticket in real-time (ordered by createdAt). */
export function watchTicketMessages(
  ticketId: string,
  cb: (messages: TicketMessage[]) => void,
): () => void {
  const q = query(
    collection(db, 'tickets', ticketId, 'messages'),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as TicketMessage))),
    () => cb([]),
  );
}

/** Admin: watch ALL tickets across all orgs, ordered by updatedAt. */
export function watchAllTickets(
  cb: (tickets: Ticket[]) => void,
): () => void {
  const q = query(
    collection(db, 'tickets'),
    orderBy('updatedAt', 'desc'),
    limit(200),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as Ticket))),
    () => cb([]),
  );
}

/** Admin: watch tickets filtered by status across all orgs. */
export function watchAllTicketsByStatus(
  status: TicketStatus,
  cb: (tickets: Ticket[]) => void,
): () => void {
  const q = query(
    collection(db, 'tickets'),
    where('status', '==', status),
    orderBy('updatedAt', 'desc'),
    limit(200),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as Ticket))),
    () => cb([]),
  );
}
